<script>
import { createBranchAdmin, updateBranchAdmin, checkAdminEmail } from '@/api/branch'

/**
 * 지점 관리자 등록/수정 다이얼로그 컴포넌트.
 *
 * Props:
 *   modelValue    - 다이얼로그 open 여부 (v-model)
 *   mode          - 'create' | 'edit'
 *   branchCode    - 대상 지점코드
 *   admin         - 수정 시 기존 관리자 데이터
 *
 * Emits:
 *   update:modelValue  - 다이얼로그 닫기
 *   saved              - 저장 완료 후 부모 목록 새로고침 트리거
 */
export default {
  name: 'BranchAdminDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    mode: { type: String, default: 'create' }, // 'create' | 'edit'
    branchCode: { type: String, required: true },
    admin: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      loading: false,
      errorMsg: '',
      /** 이메일 중복 검사 결과 메시지 (사용가능/중복) */
      emailCheckMsg: '',
      emailCheckLoading: false,
      form: {
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPosition: '',
        adminPhone: '',
      },
    }
  },
  computed: {
    isCreate() {
      return this.mode === 'create'
    },
    dialogTitle() {
      return this.isCreate ? '관리자 등록' : '관리자 수정'
    },
  },
  watch: {
    /** 다이얼로그 열릴 때 폼 초기화 */
    modelValue(val) {
      if (val) {
        this.errorMsg = ''
        this.emailCheckMsg = ''
        this.initForm()
      }
    },
  },
  methods: {
    /** 폼 초기화 */
    initForm() {
      if (this.isCreate) {
        this.form = { adminName: '', adminEmail: '', adminPassword: '', adminPosition: '', adminPhone: '' }
      } else if (this.admin) {
        this.form = {
          adminName: this.admin.branchAdminName || '',
          adminEmail: this.admin.branchAdminEmail || '',
          adminPassword: '',
          adminPosition: this.admin.branchAdminPosition || '',
          adminPhone: this.admin.branchAdminPhone || '',
        }
      }
    },

    /** 이메일(아이디) 중복 검사 */
    async handleCheckEmail() {
      this.emailCheckMsg = ''
      this.errorMsg = ''
      const email = this.form.adminEmail?.trim()
      if (!email) {
        this.emailCheckMsg = '이메일을 입력한 후 중복 확인해 주세요.'
        return
      }
      this.emailCheckLoading = true
      try {
        const res = await checkAdminEmail(email)
        if (res.data.code === 0) {
          this.emailCheckMsg = '사용 가능한 이메일입니다.'
        } else {
          this.emailCheckMsg = res.data.message || '이미 등록된 이메일입니다.'
        }
      } catch (e) {
        this.emailCheckMsg = e?.response?.data?.message || '중복 검사에 실패했습니다.'
      } finally {
        this.emailCheckLoading = false
      }
    },

    /** 저장 처리 */
    async handleSave() {
      this.errorMsg = ''

      if (!this.form.adminName) {
        this.errorMsg = '이름은 필수입니다.'
        return
      }
      if (this.isCreate && (!this.form.adminEmail || !this.form.adminPassword)) {
        this.errorMsg = '이메일과 비밀번호는 필수입니다.'
        return
      }

      this.loading = true
      try {
        if (this.isCreate) {
          const res = await createBranchAdmin(this.branchCode, {
            adminName: this.form.adminName,
            adminEmail: this.form.adminEmail,
            adminPassword: this.form.adminPassword,
            adminPosition: this.form.adminPosition,
            adminPhone: this.form.adminPhone,
          })
          if (res.data.code !== 0) {
            this.errorMsg = res.data.message || '등록에 실패했습니다.'
            return
          }
        } else {
          const res = await updateBranchAdmin(this.branchCode, this.admin.branchAdminCode, {
            adminName: this.form.adminName,
            adminPosition: this.form.adminPosition,
            adminPhone: this.form.adminPhone,
          })
          if (res.data.code !== 0) {
            this.errorMsg = res.data.message || '수정에 실패했습니다.'
            return
          }
        }
        this.$emit('saved')
        this.$emit('update:modelValue', false)
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    handleClose() {
      this.$emit('update:modelValue', false)
    },
  },
}
</script>

<template>
  <v-dialog :model-value="modelValue" max-width="480" persistent @update:model-value="handleClose">
    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">
        <v-icon size="18" color="indigo">mdi-account-tie-outline</v-icon>
        {{ dialogTitle }}
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-5">
        <v-alert v-if="errorMsg" type="error" density="compact" class="mb-4">{{ errorMsg }}</v-alert>

        <v-row dense>
          <v-col cols="12" sm="7">
            <v-text-field
              v-model="form.adminName"
              label="이름 *"
              variant="outlined"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="form.adminPosition"
              label="직책"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 이메일(로그인 ID): 등록 시 중복 확인 버튼, 수정 시 읽기전용 -->
          <v-col cols="12">
            <div class="d-flex align-center ga-2">
              <v-text-field
                v-model="form.adminEmail"
                label="이메일 (로그인 ID)"
                :readonly="!isCreate"
                :variant="isCreate ? 'outlined' : 'filled'"
                density="compact"
                hide-details="auto"
                class="flex-grow-1"
                @blur="isCreate && (emailCheckMsg = '')"
              />
              <v-btn
                v-if="isCreate"
                variant="tonal"
                color="indigo"
                size="small"
                :loading="emailCheckLoading"
                :disabled="!form.adminEmail?.trim()"
                @click="handleCheckEmail"
              >
                중복 확인
              </v-btn>
            </div>
            <div v-if="emailCheckMsg" class="text-caption mt-1" :class="emailCheckMsg.includes('사용 가능') ? 'text-success' : 'text-error'">
              {{ emailCheckMsg }}
            </div>
          </v-col>
          <!-- 비밀번호: 등록 시만 표시 -->
          <v-col v-if="isCreate" cols="12">
            <v-text-field
              v-model="form.adminPassword"
              label="초기 비밀번호 *"
              type="password"
              variant="outlined"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.adminPhone"
              label="전화번호"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" :disabled="loading" @click="handleClose">취소</v-btn>
        <v-btn color="indigo" variant="flat" rounded="lg" :loading="loading" @click="handleSave">
          {{ isCreate ? '등록' : '저장' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
